import { Card } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import ChevronDownIcon from "../../../assets/images/ChevronDownIcon";
import ChevronUpIcon from "../../../assets/images/ChevronUpIcon";
import { calculateTotalBcrsDeposit } from "../../../common/helpers/bcrsHelper";
import { displayCentsWithDollarSign } from "../../../common/helpers/stringUtils";
import { GST_REGEX, SERVICE_CHARGE_REGEX, findTaxEntry } from "../../../common/helpers/taxHelper";
import Condition from "../../../components/Conditional/Condition";
import { useConfigContext } from "../../../contexts/ConfigContext/ConfigContext";
import { usePaymentContext } from "../../../contexts/PaymentContext/PaymentContext";
import "./PaymentDetail.scss";
import PaymentDetailItem from "./PaymentDetailItem";
import PaymentDetailTakeawayItem from "./PaymentDetailTakeawayItem";

interface IPaymentDetailProps {
	defaultExpanded?: boolean;
}

const PaymentDetail: React.FC<IPaymentDetailProps> = ({ defaultExpanded = false }) => {
	const { t } = useTranslation();
	const { state: paymentData } = usePaymentContext((state) => state.paymentData);
	const { state: bcrs_deposit_rate } = useConfigContext((state) => state.config.bcrs_deposit_rate);
	const [showDetails, setShowDetails] = useState(defaultExpanded);

	const takeaway_fee = paymentData?.orders_figures.takeaway_fee || 0;
	const subtotal_value = paymentData?.orders_figures.subtotal_value || 0;
	const net_value = paymentData?.orders_figures.net_value || 0;
	const serviceChargeTax = findTaxEntry(paymentData?.tax, SERVICE_CHARGE_REGEX);
	const gstTax = findTaxEntry(paymentData?.tax, GST_REGEX);

	// Calculate total BCRS deposit amount using helper
	const totalBcrsDeposit = useMemo(() => {
		if (!paymentData?.item_info) return 0;

		return calculateTotalBcrsDeposit(paymentData.item_info, bcrs_deposit_rate);
	}, [paymentData?.item_info, bcrs_deposit_rate]);

	const handleToggleDetails = useCallback(() => {
		setShowDetails((prev) => !prev);
	}, []);

	return (
		<div className="PaymentDetailContainer">
			<div className="OrderSummaryHeader">
				<div className="SummaryRow">
					<span className="SummaryLabel">{t("orderConfirmation.totalOrder")}</span>
					<span className="SummaryValue">{displayCentsWithDollarSign(net_value)}</span>
				</div>
				<Condition check={!!gstTax}>
					<div className="SummaryRowGST GstRow">
						<div className="SummaryLabel">{gstTax?.name}</div>
					</div>
				</Condition>
				<Condition check={!!serviceChargeTax}>
					<div className="SummaryRowGST ServiceChargeRow">
						<div className="SummaryLabel">{serviceChargeTax?.name}</div>
					</div>
				</Condition>
			</div>

			<button className="DetailsLink" onClick={handleToggleDetails}>
				<span className="DetailsLinkLabel">{t("orderConfirmation.viewDetails")}</span>
				<Condition check={showDetails}>
					<ChevronUpIcon />
				</Condition>
				<Condition check={!showDetails}>
					<ChevronDownIcon />
				</Condition>
			</button>

			<Condition check={showDetails}>
				<Card className="PaymentDetail">
					<div className="PaymentDetailSection">
						<ul className="CartContainer">
							{/* dev data can carry items without an id — the position is the stable fallback */}
							{paymentData?.item_info?.map((item, index) => (
								<PaymentDetailItem key={item.id ?? `position-${index}`} item={item} />
							))}

							<Condition check={takeaway_fee > 0}>
								<PaymentDetailTakeawayItem takeawayFee={takeaway_fee} />
							</Condition>
						</ul>

						<div className="PaymentDetailDivider" />

						<div className="PaymentDetailSubtotal">
							<div className="SummaryRow">
								<span className="SummaryLabel">{t("orderConfirmation.subtotal")}</span>
								<span className="SummaryValue">{displayCentsWithDollarSign(subtotal_value)}</span>
							</div>
							<Condition check={!!serviceChargeTax}>
								<div className="SummaryRow ServiceChargeRow">
									<span className="SummaryLabel">{serviceChargeTax?.name}</span>
									<span className="SummaryValue">
										{displayCentsWithDollarSign(serviceChargeTax?.amount ?? 0)}
									</span>
								</div>
							</Condition>
							<Condition check={!!gstTax}>
								<div className="SummaryRow GstRow">
									<span className="SummaryLabel">{gstTax?.name}</span>
									<span className="SummaryValue">
										{displayCentsWithDollarSign(gstTax?.amount ?? 0)}
									</span>
								</div>
							</Condition>
							<Condition check={totalBcrsDeposit > 0}>
								<div className="SummaryRowGST">
									<span className="SummaryLabel">{t("paymentPage.bcrs.totalDeposit")}</span>
									<span className="SummaryValue">
										{displayCentsWithDollarSign(totalBcrsDeposit)}
									</span>
								</div>
								<div className="SummaryRowBCRS BCRSRow">
									<span className="BcrsDisclaimer">{t("cartPage.bcrs.disclaimer")}</span>
								</div>
							</Condition>
							<div className="SummaryRow FinalTotalRow">
								<span className="SummaryLabel">{t("orderConfirmation.total")}</span>
								<span className="SummaryValue">{displayCentsWithDollarSign(net_value)}</span>
							</div>
						</div>
					</div>
				</Card>
			</Condition>
		</div>
	);
};

export default PaymentDetail;
